import { ARRANGEMENT_TYPES, isArrangementType, isCurrentBookletDraft, type ArrangementType } from '@pams/shared';
import { typedListHref } from '@/lib/templateLinks';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [TYPED-BACK] WHICH LIST AN ORDER WAS OPENED FROM, CARRIED IN ITS URL AS `?from=`.
 *
 *  An order opened from a typed list (`/pa/durga-puja-2026`, the sidebar's "Durga Puja PA") goes
 *  back to THAT list — its page, sort and filters included — and never to Manage Arrangements,
 *  which does not even list a typed order. The record page, the builder and every hop between them
 *  carry the parameter forward (`withListOrigin`), and each back link, and each act that leaves the
 *  order (discard, publish, delete), reads it here.
 *
 *  ⚠️ [amended TYPE-FILTER] "DOES NOT EVEN LIST A TYPED ORDER" IS NO LONGER TRUE: Manage Arrangements
 *  lists every order by default ("Order type": All). So an order with NO `from` — a pasted link, the
 *  bell, the dashboard — goes back to Manage Arrangements whatever its type (the user's decision,
 *  2026-10-13), and its own typed list is no longer the fallback. `from` itself is unchanged: an order
 *  opened from the typed list still returns there.
 *
 *  ⚠️ A QUERY PARAMETER, NEVER HISTORY. `history.back()` and `navigate(-1)` are banned ([D4.3]): a
 *  page arrived at from a pasted link, the bell or a reload has no list behind it. The parameter
 *  survives all three, and copying the address copies the way back with it.
 *
 *  ⚠️ IT ONLY EVER NAMES A REGISTERED TYPED LIST — a value that leaves the site, names another path
 *  or names an unregistered type reads as NO origin, and the page falls back to its own rule. A
 *  parameter that could hold any path would be an open redirect on a police system. The list's
 *  query string is kept (it is that list's own state) and a nested `from` is dropped.
 *
 *  ⚠️ THE LIST'S QUERY MUST COME FROM ITS TABLE, NOT FROM THE ROUTER. nuqs writes a list's filters
 *  to the URL shallowly — react-router's `useLocation()` never sees them ([ANALYTICS §5], measured)
 *  — so the list builds its origin from `useServerTable`'s own serialised state.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
export const LIST_ORIGIN_PARAM = 'from';

export type ListOrigin = {
  readonly type: ArrangementType;
  /** The list's path alone — what the sidebar matches. */
  readonly pathname: string;
  /** The list's own address, its page, sort and filters included — where a back link goes. */
  readonly to: string;
  /** What a back link says: the list's name, as the sidebar says it. */
  readonly label: string;
};

/** A fixed, unreachable origin: a value that resolves anywhere else named another site. */
const BASE = 'https://pams.invalid';

const TYPED_LIST_PATH = /^\/pa\/([^/]+)$/;

const originOf = (type: ArrangementType, query: URLSearchParams): ListOrigin => {
  query.delete(LIST_ORIGIN_PARAM);
  const pathname = typedListHref(type);
  const search = query.toString();
  return {
    type,
    pathname,
    to: search === '' ? pathname : `${pathname}?${search}`,
    label: ARRANGEMENT_TYPES[type].label,
  };
};

/** The list this page was opened from, read from its own query string — `null` for none, or for anything else. */
export const listOriginOf = (search: string | URLSearchParams): ListOrigin | null => {
  const raw = (typeof search === 'string' ? new URLSearchParams(search) : search).get(LIST_ORIGIN_PARAM);
  if (raw === null || !raw.startsWith('/') || raw.startsWith('//') || raw.includes('\\')) return null;
  let url: URL;
  try {
    url = new URL(raw, BASE);
  } catch {
    return null;
  }
  if (url.origin !== BASE) return null;
  const type = TYPED_LIST_PATH.exec(url.pathname)?.[1];
  return type !== undefined && isArrangementType(type) ? originOf(type, url.searchParams) : null;
};

/**
 * A typed list's own origin, built by the list itself from its table's canonical query (`?page=2…`, or `''`).
 * ⚠️ Pass `useServerTable`'s `urlSearch`, never `useLocation().search` — see the header.
 */
export const listOriginFor = (type: ArrangementType, search: string): ListOrigin =>
  originOf(type, new URLSearchParams(search.startsWith('?') ? search.slice(1) : search));

/** `path` with the origin carried forward — unchanged when there is none. Any query `path` already has is kept. */
export const withListOrigin = (path: string, origin: ListOrigin | null): string => {
  if (origin === null) return path;
  const at = path.indexOf('?');
  const pathname = at === -1 ? path : path.slice(0, at);
  const query = new URLSearchParams(at === -1 ? '' : path.slice(at + 1));
  query.set(LIST_ORIGIN_PARAM, origin.to);
  return `${pathname}?${query.toString()}`;
};

/**
 * Where an order goes when an act LEAVES it — discarded, published, deleted: the list it was opened from,
 * else Manage Arrangements. ⚠️ [amended TYPE-FILTER] A typed order with no origin went to its own type's list,
 * because Manage Arrangements did not list one; it does now, by default, so every order lands there. Pass the
 * origin through `originForOrder` first, so a hand-made `from` cannot send an order to a list it is not on.
 */
export const listAfterLeaving = (origin: ListOrigin | null): string => origin?.to ?? '/arrangements';

/**
 * ⚠️ [DP2026] THE ONE EXCEPTION TO "NO ORIGIN → MANAGE ARRANGEMENTS": a DRAFT made from its own type's booklet
 * (`isCurrentBookletDraft`) is not listed there until it is published, so with no `from` its type's list is where
 * it lives — and where its back link and an act that leaves it go. `null` for every other order, which keeps
 * [TYPE-FILTER]'s rule. Callers use it as the fallback: `origin ?? homeListOf(order)`. Never a substitute for the
 * origin in a builder's or record page's doors: it decides a destination, not what the address carries.
 */
export const homeListOf = (order: Parameters<typeof isCurrentBookletDraft>[0] | undefined): ListOrigin | null =>
  order !== undefined && isCurrentBookletDraft(order) && isArrangementType(order.type)
    ? listOriginFor(order.type, '')
    : null;

/**
 * The origin, kept only when it is a list THIS order belongs on — `null` otherwise. A typed list holds only
 * orders of its own type, and a corrigendum inherits its parent's, so on every path the UI builds the two
 * agree; what this refuses is a hand-made link (`/arrangements/new?from=/pa/…` would send a new, untyped order
 * "back" to a list that never shows it).
 */
export const originForOrder = (origin: ListOrigin | null, type: ArrangementType | undefined): ListOrigin | null =>
  origin !== null && origin.type === type ? origin : null;

/**
 * [TYPED-BACK] The record page's own doors into the builder say so: `?via=record`, beside the origin. The
 * builder then keeps [D4.3]'s "Back to the order" even for a draft — the operator came from that record and
 * returns to it, and the record returns to the list. Only a builder opened straight from a list row goes back to
 * the list. Nothing is added when there is no origin, so an untyped order's addresses do not change.
 */
export const VIA_PARAM = 'via';
const VIA_RECORD = 'record';

/** A record page's door into the builder: the origin carried, and `via=record` beside it — `path` itself when there is no origin. */
export const withRecordOrigin = (path: string, origin: ListOrigin | null): string => {
  if (origin === null) return path;
  const carried = withListOrigin(path, origin);
  const query = new URLSearchParams(carried.slice(carried.indexOf('?') + 1));
  query.set(VIA_PARAM, VIA_RECORD);
  return `${carried.slice(0, carried.indexOf('?'))}?${query.toString()}`;
};

/** Whether the builder was opened from its order's record page (`withRecordOrigin`). */
export const cameViaRecord = (search: string | URLSearchParams): boolean =>
  (typeof search === 'string' ? new URLSearchParams(search) : search).get(VIA_PARAM) === VIA_RECORD;
